// 🔧 follow_up_queue.js
// ✅ Purpose: Admin-facing Follow-up Queue for Let's Go Vote Sales Automation
// ✅ Features:
// - Shows contacted prospects with follow-up dates due today or overdue
// - Prioritizes oldest follow-ups first, then fit score
// - Allows admins to mark follow-up completed
// - Allows admins to skip/reschedule follow-up by 7 days
// - Does NOT send emails or social messages automatically
// - Keeps human approval before external communication
// - Uses top-of-page success/error/info notifications
// - Fully block-commented for clarity and future devs

import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import SalesPageLayout from "../../components/sales/SalesPageLayout";
import { getFunctions, httpsCallable } from "firebase/functions";

function FollowUpQueue() {

  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [workingId, setWorkingId] = useState(null);
  const [runningFollowUpManager, setRunningFollowUpManager] = useState(false);

  const showStatus = (type, message) => {
    setStatus({ type, message });
    setTimeout(() => setStatus(null), 20000);
  };

  const formatDate = (date) => date.toISOString().slice(0, 10);

  const parseDateOnly = (value) => {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  // ✅ Runs Follow-up Manager
  // ✅ Purpose: Generates follow-up drafts for due prospects and moves them to Outreach Queue
  // ✅ Safety: Creates drafts only; does not send emails or social messages
  const handleRunFollowUpManager = async () => {
    try {
      setRunningFollowUpManager(true);

      showStatus("info", "Follow-up Manager is processing due follow-ups.");

      const functions = getFunctions();
      const runFollowUpManager = httpsCallable(functions, "runFollowUpManager");

      const result = await runFollowUpManager({
        followUpLimit: 5,
      });

      console.log("✅ Follow-up Manager result:", result.data);

      await loadProspects();

      showStatus(
        "success",
        `Follow-up Manager complete. Drafted: ${result.data.metrics?.completed || 0}. Failed: ${result.data.metrics?.failed || 0}.`
      );
    } catch (error) {
      console.error("❌ Follow-up Manager failed:", error);
      showStatus("error", "Follow-up Manager failed. Check console for details.");
    } finally {
      setRunningFollowUpManager(false);
    }
  };

  const loadProspects = async () => {
    try {
      setLoading(true);

      const prospectsSnap = await getDocs(
        query(collection(db, "prospects"), orderBy("createdAt", "desc"))
      );

      setProspects(
        prospectsSnap.docs.map((prospectDoc) => ({
          id: prospectDoc.id,
          ...prospectDoc.data(),
        }))
      );
    } catch (error) {
      console.error("❌ Failed to load Follow-up Queue:", error);
      showStatus("error", "Failed to load Follow-up Queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProspects();
  }, []);

  const followUpQueue = useMemo(() => {
    return prospects
      .filter((prospect) => {
        const followUpDate = parseDateOnly(prospect.followUpDate);
        if (!followUpDate) return false;

        const isDue = followUpDate <= today;
        const isClosed =
          prospect.status === "customer" ||
          prospect.status === "not interested";

        return isDue && !isClosed;
      })
      .sort((a, b) => {
        const dateA = parseDateOnly(a.followUpDate)?.getTime() || 0;
        const dateB = parseDateOnly(b.followUpDate)?.getTime() || 0;

        if (dateA !== dateB) return dateA - dateB;

        return Number(b.fitScore || 0) - Number(a.fitScore || 0);
      });
  }, [prospects, today]);

  const handleMarkFollowUpComplete = async (prospect) => {
    const nextFollowUp = new Date();
    nextFollowUp.setDate(nextFollowUp.getDate() + 14);

    try {
      setWorkingId(prospect.id);

      await updateDoc(doc(db, "prospects", prospect.id), {
        lastFollowUpCompletedAt: serverTimestamp(),
        lastFollowUpDate: formatDate(new Date()),
        followUpDate: formatDate(nextFollowUp),
        collectionStage: "follow_up_completed",
        queueReason: "Follow-up completed and next follow-up scheduled.",
        updatedAt: serverTimestamp(),
      });

      await loadProspects();

      showStatus(
        "success",
        `${prospect.eventName} follow-up completed. Next follow-up set for ${formatDate(nextFollowUp)}.`
      );
    } catch (error) {
      console.error("❌ Failed to complete follow-up:", error);
      showStatus("error", "Failed to complete follow-up.");
    } finally {
      setWorkingId(null);
    }
  };

  const handleSnoozeFollowUp = async (prospect) => {
    const snoozeDate = new Date();
    snoozeDate.setDate(snoozeDate.getDate() + 7);

    try {
      setWorkingId(prospect.id);

      await updateDoc(doc(db, "prospects", prospect.id), {
        followUpDate: formatDate(snoozeDate),
        collectionStage: "follow_up_snoozed",
        queueReason: "Follow-up snoozed for 7 days.",
        updatedAt: serverTimestamp(),
      });

      await loadProspects();

      showStatus(
        "success",
        `${prospect.eventName} follow-up snoozed until ${formatDate(snoozeDate)}.`
      );
    } catch (error) {
      console.error("❌ Failed to snooze follow-up:", error);
      showStatus("error", "Failed to snooze follow-up.");
    } finally {
      setWorkingId(null);
    }
  };

  return (
    <SalesPageLayout
      title="Follow-up Queue"
      subtitle="Prospects that are due or overdue for manual follow-up."
      status={status}
      actions={
        <>
          <Link className="btn btn-outline-secondary" to="/admin/sales-dashboard">
            Sales Dashboard
          </Link>

          <Link className="btn btn-outline-secondary" to="/admin/outreach-queue">
            Outreach Queue
          </Link>
          <button
            type="button"
            className="btn btn-warning"
            onClick={handleRunFollowUpManager}
            disabled={runningFollowUpManager}
          >
            {runningFollowUpManager
              ? "Running Follow-up Manager..."
              : "Run Follow-up Manager"}
          </button>
          <Link className="btn btn-outline-secondary" to="/admin/prospects">
            Prospects CRM
          </Link>
        </>
      }
    >
      {loading ? (
        <div className="alert alert-info">Loading Follow-up Queue...</div>
      ) : followUpQueue.length === 0 ? (
        <div className="alert alert-info">
          No follow-ups are currently due.
        </div>
      ) : (
        <div className="row">
          {followUpQueue.map((prospect) => (
            <div className="col-12 mb-3" key={prospect.id}>
              <div className="card">
                <div className="card-header d-flex justify-content-between">
                  <strong>{prospect.eventName}</strong>
                  <span className="badge bg-warning text-dark">
                    Due: {prospect.followUpDate}
                  </span>
                </div>

                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <strong>Score:</strong> {prospect.fitScore || 0}/10
                    </div>

                    <div className="col-md-3">
                      <strong>Status:</strong> {prospect.status || "new"}
                    </div>

                    <div className="col-md-3">
                      <strong>Last Channel:</strong>{" "}
                      {prospect.lastOutreachChannel || "Unknown"}
                    </div>

                    <div className="col-md-3">
                      <strong>Last Contacted:</strong>{" "}
                      {prospect.lastContacted || "Unknown"}
                    </div>
                  </div>

                  {prospect.email && (
                    <div className="mb-2">
                      <strong>Email:</strong>{" "}
                      <a href={`mailto:${prospect.email}`}>{prospect.email}</a>
                    </div>
                  )}

                  {prospect.facebookUrl && (
                    <div className="mb-2">
                      <strong>Facebook:</strong>{" "}
                      <a href={prospect.facebookUrl} target="_blank" rel="noopener noreferrer">
                        {prospect.facebookUrl}
                      </a>
                    </div>
                  )}

                  {prospect.notes && (
                    <div className="small text-muted mb-3">
                      <strong>Notes:</strong> {prospect.notes}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn btn-success me-2"
                    onClick={() => handleMarkFollowUpComplete(prospect)}
                    disabled={workingId === prospect.id}
                  >
                    {workingId === prospect.id ? "Saving..." : "Mark Follow-up Complete"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary me-2"
                    onClick={() => handleSnoozeFollowUp(prospect)}
                    disabled={workingId === prospect.id}
                  >
                    Snooze 7 Days
                  </button>

                  <Link className="btn btn-outline-primary" to="/admin/prospects">
                    Edit in CRM
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SalesPageLayout>
  );
}

export default FollowUpQueue;